/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Stack } from '@fluentui/react/lib/Stack';
import { observer } from 'mobx-react';
import React from 'react';
import { defineMessages } from 'react-intl';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getPortalFromUrl } from 'src/core/utils/routingUtils';
import { CapabilityType } from 'src/languageStudio/core/types';
import { DataDistributionTable } from 'src/modules/modelGuidance/components/DataDistributionTable';
import { handleGetLabel, SchemaType } from 'src/modules/modelGuidance/modelGuidanceUtils/modelGuidanceUtils';
import { Distribution, ModelGuidanceStore } from 'src/modules/modelGuidance/stores/ModelGuidanceStore';
import styled from 'styled-components';

export type DistributionView = { labelName: string; testingCount: number; trainingCount: number };
type DataDistributionTabProps = {
    pivotName?: SchemaType;
    isExtractionProject: boolean;
    capabilityName: CapabilityType;
    modelGuidanceStore: ModelGuidanceStore;
    onRenderOptions?: () => React.ReactNode;
};

const messages = defineMessages({
    pageDescription: {
        id: 'modelGuidance.dataDistributionTab.pageDescription',
        defaultMessage:
            'This snapshot shows how {labelType} are distributed across your training and testing sets. This data is static and tied to your model, so it won’t update until you train again.'
    }
});

const TabContainer = styled(Stack)({ margin: '20px 0 50px 0' });

export const DataDistributionTab = observer((props: DataDistributionTabProps) => {
    const { isExtractionProject, capabilityName, pivotName = null, modelGuidanceStore, onRenderOptions } = props;

    const { localize } = useLocalization();
    const { serviceName } = getPortalFromUrl();

    const getDistribution = (dist: Distribution) => {
        if (pivotName === 'Entities') {
            return dist.examplesDistribution;
        }

        if (isExtractionProject) {
            return dist.documentsDistribution;
        }

        return dist.labelsDistribution;
    };

    const { documentsDistribution, totalLabelsDistribution } = React.useMemo(() => {
        const _documentsDistribution: DistributionView[] = modelGuidanceStore
            ? modelGuidanceStore.labelsDistribution.map(dist => ({
                  labelName: dist.labelName,
                  testingCount: getDistribution(dist).Test,
                  trainingCount: getDistribution(dist).Train
              }))
            : null;

        const _totalLabelsDistribution: DistributionView[] =
            (isExtractionProject || pivotName) && modelGuidanceStore
                ? modelGuidanceStore?.labelsDistribution.map(dist => ({
                      labelName: dist.labelName,
                      testingCount: dist.labelsDistribution.Test,
                      trainingCount: dist.labelsDistribution.Train
                  }))
                : null;

        return { documentsDistribution: _documentsDistribution, totalLabelsDistribution: _totalLabelsDistribution };
    }, [isExtractionProject, modelGuidanceStore?.labelsDistribution]);

    return (
        <TabContainer tokens={{ childrenGap: 20 }} data-automation-id="dataDistributionTab">
            <LuisText>
                {localize(messages.pageDescription, {
                    labelType: localize(handleGetLabel(pivotName, serviceName, false))
                })}
            </LuisText>

            {onRenderOptions && onRenderOptions()}

            <DataDistributionTable
                pivotName={pivotName}
                capabilityName={capabilityName}
                isExtractionProject={isExtractionProject}
                documentsDistribution={documentsDistribution}
                totalLabelsDistribution={totalLabelsDistribution}
                insufficientExamples={modelGuidanceStore?.insufficientExamples}
            />
        </TabContainer>
    );
});
